/**
 * Routes and providers.
 *
 * Hash routing, still: it is the one form of client routing that needs no
 * server rewrite rules, which is what AC-028 asks for — a clean local install
 * must work without anyone configuring a web server.
 */

import type { ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createHashRouter, Navigate, RouterProvider } from 'react-router';
import { BaseStyles, ThemeProvider } from '@primer/react';

import { StoreProvider, useStoreState } from './store-context.js';
import { Shell } from './shell.js';
import { BoardScreen } from './screens/board.js';
import { TeamScreen } from './screens/team.js';
import { AgentScreen } from './screens/agent.js';
import { RunsScreen } from './screens/runs.js';
import { RolesScreen } from './screens/settings.js';
import { SkillsScreen } from './screens/skills.js';
import { TeamEditorScreen } from './screens/team-editor.js';
import { BoardSettingsScreen } from './screens/board-settings.js';
import { OperatorScreen } from './screens/operator.js';
import { ConnectionsScreen } from './screens/connections.js';
import { CredentialsScreen } from './screens/credentials.js';
import { McpAccessScreen } from './screens/mcp-access.js';
import { UsageScreen } from './screens/usage.js';
import { QuorumsScreen } from './screens/quorums.js';
import { DeploymentDetailScreen, ReleaseDetailScreen, ReleasesScreen } from './screens/releases.js';
import { WebhooksScreen } from './screens/webhooks.js';
import { EpicsScreen } from './screens/epics.js';
import { ProposalsScreen } from './screens/proposals.js';
import { EpicCoordinatorSettingsScreen } from './screens/epic-coordinator-settings.js';
import { readConfig } from '../store.js';
import { FirebaseAuthGate } from './firebase-auth-gate.js';

const client = new QueryClient({
  defaultOptions: {
    queries: {
      /*
       * Reads are cheap and local, and a Console showing a stale roster is
       * worse than one that re-fetches. Refetching on window focus is the
       * behaviour an operator expects from a dashboard they left open.
       */
      staleTime: 5_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});

const router = createHashRouter([
  {
    path: '/',
    element: <Shell />,
    children: [
      { index: true, element: <Navigate to="/board" replace /> },
      { path: 'board', element: <BoardScreen /> },
      // The open card is a route, so it can be linked to. See board.tsx.
      { path: 'board/:id', element: <BoardScreen /> },
      { path: 'board/:id/chat', element: <BoardScreen /> },
      { path: 'epics', element: <EpicsScreen /> },
      { path: 'epics/:id', element: <EpicsScreen /> },
      // The open conversation is a route for the same reason.
      { path: 'operator', element: <OperatorScreen /> },
      { path: 'operator/:id', element: <OperatorScreen /> },
      { path: 'overview', element: <TeamScreen /> },
      { path: 'agents/:id', element: <AgentScreen /> },
      // Human Review is canonical on the Board; keep old bookmarks useful.
      { path: 'decisions', element: <Navigate to="/board?stage=human-review" replace /> },
      { path: 'proposals', element: <ProposalsScreen /> },
      { path: 'releases', element: <ReleasesScreen /> },
      { path: 'releases/:id', element: <ReleaseDetailScreen /> },
      { path: 'deployments/:id', element: <DeploymentDetailScreen /> },
      { path: 'runs/:id', element: <RunsScreen /> },
      /*
       * Two screens became one and one moved (ADR-118). A bookmark to either
       * index lands on what replaced it, rather than on the board via the
       * catch-all — which would look like the link was simply wrong.
       */
      { path: 'agents', element: <Navigate to="/overview" replace /> },
      { path: 'runs', element: <Navigate to="/board" replace /> },
      { path: 'team-editor', element: <TeamEditorScreen /> },
      { path: 'epic-coordinator', element: <EpicCoordinatorSettingsScreen /> },
      { path: 'board-settings', element: <BoardSettingsScreen /> },
      { path: 'credentials', element: <CredentialsScreen /> },
      { path: 'connections', element: <ConnectionsScreen /> },
      { path: 'webhooks', element: <WebhooksScreen /> },
      { path: 'mcp-access', element: <McpAccessScreen /> },
      { path: 'usage', element: <UsageScreen /> },
      { path: 'quorums', element: <QuorumsScreen /> },
      // The read-only roster keeps its own route; the editor took the menu entry.
      { path: 'roles', element: <RolesScreen /> },
      { path: 'skills', element: <SkillsScreen /> },
      // A bad bookmark lands where work starts, rather than on an error.
      { path: '*', element: <Navigate to="/board" replace /> },
    ],
  },
]);

/*
 * Primer's theme follows the console's own switch, not the OS on its own:
 * ThemeProvider left on `auto` while the page was pinned light painted the
 * dark palette's ink onto a white page on any dark-mode machine.
 */
function PrimerTheme({ children }: { children: ReactNode }): ReactNode {
  const theme = useStoreState((state) => state.theme);
  return (
    <ThemeProvider colorMode={theme === 'system' ? 'auto' : theme === 'dark' ? 'night' : 'day'}>
      <BaseStyles>{children}</BaseStyles>
    </ThemeProvider>
  );
}

export function App(): ReactNode {
  const config = readConfig();
  return (
    <StoreProvider>
      <PrimerTheme>
        <FirebaseAuthGate config={config}>
          <QueryClientProvider client={client}>
            <RouterProvider router={router} />
          </QueryClientProvider>
        </FirebaseAuthGate>
      </PrimerTheme>
    </StoreProvider>
  );
}
